<template>
  <div>
    <validation-observer ref="otherConfigRules">
      <b-form>
        <h5 class="section-label mb-2 mt-2">
          {{ $t('Other Configurations') }}
        </h5>
        <b-row class="match-height mt-1 mb-2">
          <b-col md="6">
            <InputField
              id="maximum-password-attempts"
              v-model="otherConfiguration.maximumPasswordAttempts"
              :label="$t('Maximum Password Attempts')"
              :placeholder="$t('Enter maximum password attempts')"
              type="text"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="maximum-pin-attempts"
              v-model="otherConfiguration.maxTrxPINAttempt"
              :label="$t('Maximum PIN Attempts')"
              :placeholder="$t('Enter maximum pin attempts')"
              type="text"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="session-timeout-min"
              v-model="otherConfiguration.sessionTimeoutMin"
              :label="$t('Session Timeout (Minute)')"
              :placeholder="$t('Enter Session Timeout')"
              type="number"
              :error-name="$t('Session Timeout')"
              rules="integer|positive"
              for="session-timeout-min"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="supported-card-schemes"
              v-model="otherConfiguration.supportedCardSchemes"
              :reduce="option => option.value"
              :label="$t('Supported Card Schemes')"
              :options="cardTypesOptions"
              :placeholder="$t('Select Schemes')"
              item="label"
              multiple
              :readonly="isReadonly"
              class="select-schemes"
            />
          </b-col>
        </b-row>
      </b-form>
      <FullScreenSpinner :show="showSpinner" />
    </validation-observer>
    <div class="mt-2">
      <Button
        v-if="getUserPermission('UPDATE_SECURITY_CONFIG')"
        :label="$t('Update')"
        :loading="isLoading"
        :disabled="isDisabled"
        size="lg"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions } from 'vuex'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '@/helpers/permissions'
import { cardTypes } from '@/helpers/data'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'

export default {
  components: {
    BRow,
    BCol,
    BForm,
    ValidationObserver,
    Button,
    FullScreenSpinner,
    InputField,
    SelectField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],

  data() {
    return {
      isLoading: false,
      isDisabled: false,
      otherConfiguration: {
        maximumPasswordAttempts: '',
        maxTrxPINAttempt: '',
        sessionTimeoutMin: 0,
        supportedCardSchemes: [],
      },
      cardTypesOptions: cardTypes,
    }
  },
  mounted() {
    this.fetchOtherConfigurations()
  },
  methods: {
    ...mapActions({
      getFraudChecks: 'eChannels/getFraudChecksList',
      updateFruadChecks: 'eChannels/updateFraudCheckAction',
    }),
    async fetchOtherConfigurations() {
      try {
        this.showSpinner = true
        const response = await this.getFraudChecks({
          permission: eChannelsPermission.VIEW_SECURITY_CONFIG,
        })
        const { status, data } = response
        if (status === 200) {
          this.otherConfiguration = {
            ...data,
            supportedCardSchemes: data.supportedCardSchemes ? data.supportedCardSchemes.split(',') : [],
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async otherConfigAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          maximumPasswordAttempts: this.otherConfiguration.maximumPasswordAttempts ? Number(this.otherConfiguration.maximumPasswordAttempts) : '',
          maxTrxPINAttempt: this.otherConfiguration.maxTrxPINAttempt ? Number(this.otherConfiguration.maxTrxPINAttempt) : '',
          sessionTimeoutMin: this.otherConfiguration.sessionTimeoutMin,
          supportedCardSchemes: this.otherConfiguration.supportedCardSchemes.toString(),
        }
        const response = await this.updateFruadChecks({
          formData, permission: eChannelsPermission.UPDATE_SECURITY_CONFIG,
        })
        const { status } = await response
        if (status === 200) {
          this.hideModal('modal-lg')
          this.alertSuccess({ text: this.$t('Your request to Update Other Configrations was successful') })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your request to Update Other Configrations failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async handleSubmit() {
      this.$refs.otherConfigRules.validate().then(async success => {
        if (success) {
          this.confirm(this.otherConfigAction, { text: this.$t('Are you sure you want to Update Other Configrations?') })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.select-schemes {
  ::v-deep {
   color: #000 !important;
}
}
</style>
